import { useEffect, useCallback, useMemo } from "react";
import WebSocketManager from "../ws/client";

export function useRealTimeList({
  clientCode,
  clientType,
  setPagedData,
  setTotalElements,
  onMessageReceived,
  matchesFilters
}) {

  const { topic, idField } = useMemo(() => {

    let topicSuffix = "";
    let idField = "id";

    switch (clientType) {
      case "products":
      case "product":
        topicSuffix = "products"; idField = "product_id"; break;
      case "recipes":
      case "recipe":
        topicSuffix = "recipes"; idField = "recipe_id"; break;
      case "combos":
      case "combo":
        topicSuffix = "combos"; idField = "combo_id"; break;
      case "students":
        topicSuffix = "students"; idField = "id_student"; break;
      case "parents":
        topicSuffix = "parents"; idField = "login"; break;
      case "employees":
        topicSuffix = "employees"; idField = "login"; break;
      default:
        topicSuffix = clientType.toLowerCase() + "s";
        idField = "id";
    }

    return {
      topic: `/topic/clients/${clientCode}/${topicSuffix}`,
      idField
    };

  }, [clientCode, clientType]);

  const handleMessage = useCallback((event) => {

    try {
      const { type, payload } = event;
      setPagedData(prevList => {
        switch (type) {
          case "UPDATED": {
            if (matchesFilters && !matchesFilters(payload)) {
              const existed = prevList.some(item => item[idField] === payload[idField]);
              if (existed && setTotalElements) {
                setTotalElements(prev => Math.max(0, prev - 1));
              }
              return prevList.filter(item => item[idField] !== payload[idField]);
            }
            return prevList.map(item =>
              item[idField] === payload[idField]
                ? { ...item, ...payload }
                : item
            );
          }

          case "BATCH_UPDATED": {
            if (!Array.isArray(payload)) return prevList;
            const updatedMap = new Map(payload.map(item => [item[idField], item]));
            let removedCount = 0;
            const nextList = prevList.reduce((acc, item) => {
              const updatedItem = updatedMap.get(item[idField]);
              if (!updatedItem) {
                acc.push(item);
                return acc;
              }
              if (matchesFilters && !matchesFilters(updatedItem)) {
                removedCount += 1;
                return acc;
              }
              acc.push({ ...item, ...updatedItem });
              return acc;
            }, []);

            if (removedCount > 0 && setTotalElements) {
              setTotalElements(prev => Math.max(0, prev - removedCount));
            }
            return nextList;
          }


          case "DELETED":
            const targetId =
              typeof payload === "object"
                ? payload[idField]
                : payload;
            const exists = prevList.some(
              item => item[idField] === targetId
            );

            if (exists && setTotalElements) {
              setTotalElements(prev => Math.max(0, prev - 1));
            }

            return prevList.filter(
              item => item[idField] !== targetId
            );

          case "CREATED":
            if (setTotalElements) {
              setTotalElements(prev => prev + 1);
            }

            return [payload, ...prevList];


          default:
            return prevList;
        }

      });

      onMessageReceived?.(event);

    } catch (error) {

      console.error(`Erro no WebSocket (${clientType})`, error);

    }

  }, [idField, setPagedData, setTotalElements, onMessageReceived, matchesFilters, clientType]);


  useEffect(() => {

    if (!clientCode || !clientType) return;

    WebSocketManager.subscribe(topic, handleMessage);

    return () => {
      WebSocketManager.unsubscribe(topic);
    };

  }, [topic, handleMessage]);
}